import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api.js';

const manilaToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
/** Now, as a value for <input type="datetime-local"> in the browser's own zone. */
const localNow = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
const when = (ts) =>
  new Date(ts).toLocaleString('en-US', { timeZone: 'Asia/Manila', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const EMPTY = { tugId: '', crewMemberId: '', kind: 'embark', occurredAt: '', reason: '' };

/** Crew embarkation / disembarkation log: who joined or left which tug, when and why. */
export default function CrewMovementsCard({ members, tugs }) {
  const [date, setDate] = useState(manilaToday);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(() => ({ ...EMPTY, occurredAt: localNow() }));
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadSeq = useRef(0); // newest request wins when the date changes quickly
  const load = useCallback(async (d) => {
    const seq = ++loadSeq.current;
    try {
      const next = await api.listCrewMovements(d);
      if (seq === loadSeq.current) setRows(next);
    } catch (e) {
      if (seq === loadSeq.current) setMsg({ type: 'danger', text: e.message || 'Failed to load crew movements.' });
    }
  }, []);
  useEffect(() => { load(date); }, [load, date]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!form.tugId || !form.crewMemberId || !form.occurredAt) {
      setMsg({ type: 'danger', text: 'Tug, crew member and time are required.' });
      return;
    }
    setSaving(true);
    try {
      const saved = await api.addCrewMovement({
        tugId: form.tugId,
        crewMemberId: form.crewMemberId,
        kind: form.kind,
        occurredAt: new Date(form.occurredAt).toISOString(),
        reason: form.reason.trim() || null,
      });
      setMsg({ type: 'success', text: `${saved.crewName} ${saved.kind === 'embark' ? 'embarked' : 'disembarked'} recorded.` });
      setForm({ ...EMPTY, tugId: form.tugId, kind: form.kind, occurredAt: localNow() });
      await load(date);
    } catch (err) {
      setMsg({ type: 'danger', text: err.details?.[0]?.message || err.message || 'Failed to record the movement.' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setMsg(null);
    try {
      await api.deleteCrewMovement(id);
      await load(date);
    } catch (err) {
      setMsg({ type: 'danger', text: err.message || 'Failed to remove the entry.' });
    }
  };

  return (
    <div className="card border-0 shadow-sm" data-testid="crew-movements-card">
      <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
        <h5 className="mb-0 text-secondary">
          <i className="fas fa-right-left me-2" aria-hidden="true" />
          Crew Embarkation / Disembarkation
        </h5>
        <input type="date" className="form-control form-control-sm w-auto" value={date} max={manilaToday()} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Movements on date" data-testid="movement-date" />
      </div>
      <div className="card-body">
        {msg && <div className={`alert alert-${msg.type} py-2`} role="alert" data-testid="movement-message">{msg.text}</div>}
        <form className="row g-2 align-items-end mb-3" onSubmit={submit} noValidate>
          <div className="col-md-2">
            <label htmlFor="mv-tug" className="form-label small fw-bold text-muted">Tug</label>
            <select id="mv-tug" className="form-select form-select-sm" value={form.tugId} onChange={(e) => setForm({ ...form, tugId: e.target.value })} data-testid="movement-tug">
              <option value="">Select…</option>
              {tugs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label htmlFor="mv-member" className="form-label small fw-bold text-muted">Crew member</label>
            <select id="mv-member" className="form-select form-select-sm" value={form.crewMemberId} onChange={(e) => setForm({ ...form, crewMemberId: e.target.value })} data-testid="movement-member">
              <option value="">Select…</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.position}){m.compliance && !m.compliance.compliant ? ' — certificates expired, cannot embark' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label htmlFor="mv-kind" className="form-label small fw-bold text-muted">Movement</label>
            <select id="mv-kind" className="form-select form-select-sm" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="movement-kind">
              <option value="embark">Embarked</option>
              <option value="disembark">Disembarked</option>
            </select>
          </div>
          <div className="col-md-2">
            <label htmlFor="mv-time" className="form-label small fw-bold text-muted">Date &amp; time</label>
            <input id="mv-time" type="datetime-local" className="form-control form-control-sm" value={form.occurredAt} onChange={(e) => setForm({ ...form, occurredAt: e.target.value })} data-testid="movement-time" />
          </div>
          <div className="col-md-2">
            <label htmlFor="mv-reason" className="form-label small fw-bold text-muted">Reason</label>
            <input id="mv-reason" className="form-control form-control-sm" maxLength={200} placeholder="Relief, leave, medical…" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="movement-reason" />
          </div>
          <div className="col-md-1">
            <button type="submit" className="btn btn-primary btn-sm w-100" disabled={saving} data-testid="movement-save">Record</button>
          </div>
        </form>
        {rows.length === 0 ? (
          <p className="text-muted small mb-0" data-testid="no-movements">No crew movements recorded for this day.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle small mb-0">
              <thead><tr><th>Tug</th><th>Movement</th><th>Crew member</th><th>Time</th><th>Reason</th><th>Recorded by</th><th /></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid="movement-row" data-kind={r.kind} data-crew={r.crewName}>
                    <td className="fw-bold">{r.tugName}</td>
                    <td><span className={`badge ${r.kind === 'embark' ? 'bg-success' : 'bg-secondary'}`}>{r.kind === 'embark' ? 'Embarked' : 'Disembarked'}</span></td>
                    <td>{r.crewName}{r.position ? ` (${r.position})` : ''}</td>
                    <td>{when(r.occurredAt)}</td>
                    <td>{r.reason || '—'}</td>
                    <td className="text-muted">{r.recordedBy || '—'}</td>
                    <td className="text-end">
                      {r.removable && (
                        <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => remove(r.id)} data-testid="movement-remove">Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
